import { useState, useEffect, useRef, useCallback } from "react";

// Wheel-zoom (desktop) and pinch-zoom (touch) over an index range into `data`.
// Keeps the point under the cursor / pinch center stable while zooming, and
// resets to the full range whenever a new `data` array arrives (e.g. period change).
export default function useZoomPan(data, { minVisible = 10 } = {}) {
  const length = data.length;
  const [range, setRange] = useState({ start: 0, end: Math.max(length - 1, 0) });
  const containerRef = useRef(null);
  const pinchDist = useRef(null);

  useEffect(() => {
    setRange({ start: 0, end: Math.max(data.length - 1, 0) });
  }, [data]);

  const zoomAt = useCallback((pivotFrac, factor) => {
    setRange((r) => {
      const len = data.length;
      const span = r.end - r.start;
      if (span <= 0 || len <= minVisible) return r;
      const pivot = r.start + pivotFrac * span;
      let newSpan = span * factor;
      newSpan = Math.max(minVisible - 1, Math.min(len - 1, newSpan));
      let start = pivot - pivotFrac * newSpan;
      let end = start + newSpan;
      if (start < 0) { end -= start; start = 0; }
      if (end > len - 1) { start -= end - (len - 1); end = len - 1; }
      start = Math.max(0, Math.round(start));
      end = Math.min(len - 1, Math.round(end));
      if (end <= start) return r;
      return { start, end };
    });
  }, [data, minVisible]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const factor = Math.exp(e.deltaY * 0.0015);
      zoomAt(frac, factor);
    };
    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        const [a, b] = e.touches;
        pinchDist.current = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      }
    };
    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && pinchDist.current != null) {
        e.preventDefault();
        const [a, b] = e.touches;
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const rect = el.getBoundingClientRect();
        const midX = (a.clientX + b.clientX) / 2;
        const frac = Math.min(1, Math.max(0, (midX - rect.left) / rect.width));
        zoomAt(frac, pinchDist.current / dist);
        pinchDist.current = dist;
      }
    };
    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) pinchDist.current = null;
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("wheel", handleWheel);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [zoomAt]);

  const reset = useCallback(() => setRange({ start: 0, end: Math.max(data.length - 1, 0) }), [data]);
  const zoomed = range.start > 0 || range.end < length - 1;

  return { containerRef, range, setRange, reset, zoomed };
}
